import { useCallback, useEffect, useState } from 'react';
import {
  Alert, Button, Empty, Space, Table, Tag, Tooltip, Typography, message
} from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { accountAuditApi } from './accountAuditApi';

const { Text } = Typography;

// ─────────────────────────────────────────────────────────────────
// Glass-i — Account Audit Log panel (Stage 6B.7D).
//
// Read-only history panel embedded inside the Journal Voucher,
// Payment / Receipt, and Expense Entry view modals. Mounted only
// when `mode === 'view'` so the operator never sees noise on the
// add / edit surface. Fetches a single page of audit rows scoped
// to (entityType, entityId); pagination = "View more" pulls in
// the next page.
//
// Metadata is shown as a compact JSON preview — full payload is
// rendered in a tooltip so the row stays tight.
// ─────────────────────────────────────────────────────────────────

const PAGE_SIZE = 25;

function fmtTs(v) {
  if (!v) return <span className="erp-grid__muted">—</span>;
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return v;
  const iso = d.toISOString();
  return `${iso.slice(0, 10)} ${iso.slice(11, 16)}`;
}

function prettyEvent(action) {
  if (!action) return '—';
  return String(action).replace(/_/g, ' ');
}

function metaPreview(meta) {
  if (!meta || typeof meta !== 'object') return null;
  const keys = Object.keys(meta);
  if (keys.length === 0) return null;
  const pick = keys.slice(0, 3);
  const txt = pick.map((k) => {
    const v = meta[k];
    if (v === null || v === undefined) return `${k}=—`;
    const s = typeof v === 'object' ? JSON.stringify(v) : String(v);
    return `${k}=${s.length > 40 ? s.slice(0, 40) + '…' : s}`;
  }).join(' · ');
  return keys.length > 3 ? `${txt} · +${keys.length - 3}` : txt;
}

export default function AuditLogPanel({ entityType, entityId }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);

  const fetchPage = useCallback(async (p) => {
    if (!entityType || !entityId) return;
    setLoading(true);
    try {
      const data = await accountAuditApi.list({
        entityType, entityId,
        page: p, pageSize: PAGE_SIZE
      });
      setRows(p === 1 ? (data?.rows || []) : [...rows, ...(data?.rows || [])]);
      setTotal(Number(data?.total) || 0);
      setPage(p);
    } catch (e) {
      message.error(e?.response?.data?.error?.message || 'Failed to load audit log.');
    } finally {
      setLoading(false);
    }
    // rows intentionally excluded — we re-set it conditionally above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityType, entityId]);

  useEffect(() => {
    setRows([]); setPage(1); setTotal(0);
    fetchPage(1);
  }, [fetchPage]);

  const columns = [
    { title: 'Date / Time', dataIndex: 'createdAt', key: 'createdAt',
      width: 140, render: fmtTs },
    { title: 'User', key: 'user', width: 160,
      render: (_v, r) => r.userName
        ? <span>{r.userName}</span>
        : r.actorType === 'system'
          ? <Tag>system</Tag>
          : <span className="erp-grid__muted">—</span> },
    { title: 'Event', dataIndex: 'eventType', key: 'eventType', width: 200,
      render: (v) => (
        <code style={{ fontSize: 12 }}>{prettyEvent(v)}</code>
      ) },
    { title: 'Details', key: 'details', ellipsis: true,
      render: (_v, r) => {
        const summary = metaPreview(r.metadata);
        if (!summary) return <Text type="secondary">—</Text>;
        return (
          <Tooltip
            placement="topLeft"
            overlayStyle={{ maxWidth: 480 }}
            title={
              <pre style={{
                margin: 0, fontSize: 11, lineHeight: 1.5,
                color: '#e2e8f0', whiteSpace: 'pre-wrap', wordBreak: 'break-word'
              }}>
                {JSON.stringify(r.metadata, null, 2)}
              </pre>
            }
          >
            <span style={{ fontSize: 12, color: '#475569' }}>{summary}</span>
          </Tooltip>
        );
      } }
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between',
                    alignItems: 'center', marginBottom: 8 }}>
        <Text type="secondary" style={{ fontSize: 12 }}>
          Read-only history of every recorded change for this entity. Sensitive
          fields (tokens, password hashes, OTP digests) are auto-redacted.
        </Text>
        <Space>
          <Text type="secondary" style={{ fontSize: 11.5 }}>
            {total} {total === 1 ? 'entry' : 'entries'}
          </Text>
          <Button size="small" icon={<ReloadOutlined />}
            onClick={() => fetchPage(1)} loading={loading}>
            Refresh
          </Button>
        </Space>
      </div>
      <Table
        size="small"
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={rows}
        pagination={false}
        locale={{
          emptyText: <Empty description="No audit history yet for this entity." />
        }}
      />
      {rows.length < total ? (
        <div style={{ textAlign: 'center', marginTop: 10 }}>
          <Button size="small" loading={loading}
            onClick={() => fetchPage(page + 1)}>
            Load older entries ({total - rows.length} remaining)
          </Button>
        </div>
      ) : null}
    </div>
  );
}
